import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Badge,
} from "reactstrap";
import {
  FaCheckCircle,
  FaCalendarAlt,
  FaEdit,
  FaFilePdf,
  FaFilter,
  FaPlus,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUser,
  FaReceipt,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { debounce } from "lodash";
import { customerReturnActions } from "../../sagas/customerReturnSlice";
import { formatPrice } from "../../helpers/utility";
import CustomerReturnForm from "./CustomerReturnForm";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import POSHeader from "./POSHeader";
import POSKeypadModal from "./POSKeypadModal";
import { CBL_PAGE, CPMT_INVOICES_PAGE, CPMT_PRODUCTS_PAGE, CPMT_RECEPTION_PAGE, CPMT_STOCK_PAGE, CPMT_TRANSFER_PAGE, PAY_PAGE, RETOUR_FORM_EDIT, RETOUR_FORM_NEW, RETOUR_PAGE } from "../../routes/routeConstants";
import { useNavigate } from "react-router-dom";

import "./RetourClientListPOS.css";

const RetourClientList = () => {
  const dispatch = useDispatch();
  const { customerReturns, loading } = useSelector((state) => state.customerReturns);
  const { t } = useTranslation();
  const { company } = useSelector((s) => s.auth);
  const { businessName, email, address, telephone, fullName, commercialId, pointOfSaleId } = useSelector((state) => state.auth);

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("RETOURS");

  const [showModal, setShowModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);

  const now = new Date();
  const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const currentDay = startOfWeek.getDay();
  const diffToMonday = currentDay === 0 ? -6 : 1 - currentDay;
  startOfWeek.setDate(startOfWeek.getDate() + diffToMonday);
  const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => n.toString().padStart(2, "0");
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const searchInputRef = useRef(null);
  const [showSearchKeypad, setShowSearchKeypad] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    clientId: "",
    commercialId:commercialId,
    dateFrom: formatDate(startOfWeek),
    dateTo: formatDate(tomorrowDate),
    query: "",
  });

  const latestFiltersRef = useRef(filters);

  useEffect(() => {
    // F2 open keypad
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        setShowSearchKeypad(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    dispatch(customerReturnActions.list({ ...filters }));
  }, [filters]);

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        dispatch(
          customerReturnActions.list({
            ...latestFiltersRef.current,
            query,
          })
        );
      }, 250),
    [dispatch]
  );

  useEffect(() => {
    latestFiltersRef.current = filters;
  }, [filters]);

  const clearFilters = () => {
    const cleared = {
      query: "",
      clientId: "",
      dateFrom: formatDate(startOfWeek),
      dateTo: formatDate(tomorrowDate),
    };
    setFilters(cleared);
    dispatch(customerReturnActions.list(cleared));
  };

  const handleShowModal = () => {
    setSelectedReturn(null);
    setShowModal(true);
  };

  const handleEdit = (returnItem) => {
    navigate(RETOUR_FORM_EDIT, {
      state: {
        customerReturn: returnItem
      }
    });
  };

  const handleCloseModal = () => {
    navigate(RETOUR_PAGE);
  };

  // ===== PDF RECEIPT (keep your original logic) =====
  const fmt = (n) => {
    const x = Number(n || 0);
    const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
    return s.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };
  const safe = (v, fb = "-") => (v || v === 0 ? v : fb);
  const toDateFR = (d) => {
    try {
      return new Date(d).toLocaleDateString("fr-FR");
    } catch {
      return "-";
    }
  };

  const generateReturnReceipt = (returnItem, t = (k) => k, opts = {}) => {
    const { devise = "DH", open = true, fileName = `BON_RETOUR_${safe(returnItem?.numeroRetour, returnItem?.id)}.pdf` } = opts;

    const rawLines = returnItem?.lignes || returnItem?.lignesRetour || returnItem?.lines || [];
    const rows = rawLines.map((ln) => {
      const code = ln.article?.code || ln.code || ln.sku || "";
      const name = ln.article?.libelle || ln.designation || ln.libelle || "";
      const qte = Number(ln.quantiteRetournee || ln.qte || ln.quantity || 0);
      const tva = ln.tauxTVA ?? ln.tva ?? 0.2;

      const puHT =
        ln.prixUnitaireHT ??
        ln.puHT ??
        (ln.prixUnitaireTTC ? Number(ln.prixUnitaireTTC) / (1 + Number(tva || 0)) : 0);

      const totalHT = puHT * qte;
      const totalTVA = totalHT * Number(tva || 0);
      const totalTTC = ln.montantTTC ?? totalHT + totalTVA;

      return { code, name, qte, puHT, tva, totalHT, totalTTC };
    });

    const totalHT = Number(returnItem?.montantHT ?? rows.reduce((s, r) => s + r.totalHT, 0));
    const totalTTC = Number(returnItem?.montantTTC ?? rows.reduce((s, r) => s + r.totalTTC, 0));
    const totalTVA = Math.max(0, totalTTC - totalHT);

    const doc = new jsPDF({ unit: "pt", format: "a4" });

    const primary = [6, 145, 43]; // #06912b
    const ink = [15, 23, 42]; // #0F172A
    const subtle = [100, 116, 139];
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...ink);
    doc.text(safe(company?.name, "Entreprise"), 40, 50);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...subtle);
    const addrLines = [
      safe(company?.address, ""),
      [company?.phone1, company?.phone2].filter(Boolean).length
        ? `Tél: ${[company.phone1, company.phone2].filter(Boolean).join(" / ")}`
        : "",
      safe(company?.email, ""),
    ].filter(Boolean);

    let y = 70;
    addrLines.forEach((line) => {
      doc.text(String(line), 40, y);
      y += 14;
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...primary);
    doc.text("BON DE RETOUR", pageWidth - 20, 40, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...subtle);
    doc.text(`# ${safe(returnItem?.numeroRetour, returnItem?.id)}`, pageWidth - 20, 58, { align: "right" });
    doc.text(toDateFR(returnItem?.dateOperation || new Date()), pageWidth - 20, 72, { align: "right" });

    const clientName =
      returnItem?.client?.entreprise ||
      [returnItem?.client?.prenom, returnItem?.client?.nom].filter(Boolean).join(" ") ||
      safe(returnItem?.clientNom, "-");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...ink);
    doc.text("Client", 40, 120);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...ink);
    doc.text(safe(clientName, "-"), 40, 138);

    const modeMap = { AVOIR: "Avoir", REMBOURSEMENT: "Remboursement", ECHANGE: "Échange" };
    const statutMap = { BROUILLON: "Brouillon", VALIDE: "Validé", ANNULE: "Annulé", COMPTABILISE: "Comptabilisé" };

    const modeTxt = modeMap[returnItem?.modeRemboursement] || safe(returnItem?.modeRemboursement, "-");
    const statutTxt = statutMap[returnItem?.statut] || safe(returnItem?.statut, "-");

    doc.setTextColor(...subtle);
    doc.text(`Mode: ${modeTxt}`, 40, 160);
    doc.text(`Statut: ${statutTxt}`, 40, 174);

    const columns = [
      { header: "Code", dataKey: "code" },
      { header: "Désignation", dataKey: "name" },
      { header: "Qté", dataKey: "qte" },
      { header: "PU HT", dataKey: "puHT" },
      { header: "TVA", dataKey: "tva" },
      { header: "Total TTC", dataKey: "totalTTC" },
    ];

    const data = rows.map((r) => ({
      code: r.code,
      name: r.name,
      qte: r.qte,
      puHT: `${fmt(r.puHT)} ${devise}`,
      tva: `${Math.round(Number(r.tva || 0) * 100)}%`,
      totalTTC: `${fmt(r.totalTTC)} ${devise}`,
    }));

    autoTable(doc, {
      startY: 200,
      head: [columns.map((c) => c.header)],
      body: data.map((row) => columns.map((c) => row[c.dataKey])),
      styles: { fontSize: 9, cellPadding: 6 },
      headStyles: { fillColor: primary, textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      theme: "striped",
      columnStyles: {
        2: { halign: "right", cellWidth: 50 },
        3: { halign: "right", cellWidth: 80 },
        4: { halign: "center", cellWidth: 60 },
        5: { halign: "right", cellWidth: 90 },
      },
    });

    const endY = doc.lastAutoTable.finalY || 200;
    const recapX = 300;
    let ry = endY + 24;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...ink);
    doc.text("Récapitulatif", recapX, ry);
    ry += 16;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...ink);
    doc.text(`Total HT:`, recapX, ry);
    doc.text(`${fmt(totalHT)} ${devise}`, 540, ry, { align: "right" });
    ry += 14;

    doc.text(`TVA:`, recapX, ry);
    doc.text(`${fmt(totalTVA)} ${devise}`, 540, ry, { align: "right" });
    ry += 14;

    doc.setFont("helvetica", "bold");
    doc.text(`Total TTC:`, recapX, ry);
    doc.text(`${fmt(totalTTC)} ${devise}`, 540, ry, { align: "right" });
    ry += 20;

    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...subtle);
    doc.text("Merci pour votre confiance. Ce reçu atteste le retour des articles indiqués.", 40, ry);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...subtle);
    doc.text(safe(company?.name, "Entreprise"), 40, 820);
    doc.text(`Document généré le ${toDateFR(new Date())}`, 540, 820, { align: "right" });

    if (open) doc.output("dataurlnewwindow");
    else doc.save(fileName);
  };

  const handleValidateReturn = (returnItem) => {
    Swal.fire({
      title: t("customerReturns.confirmValidate"),
      text: t("customerReturns.confirmValidateMessage"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("common.yes"),
      cancelButtonText: t("common.no"),
    }).then((result) => {
      if (!result.isConfirmed) return;

      const payload = {
        id: returnItem.id,
        onSuccess: () => {
          Swal.fire({
            icon: "success",
            title: t("common.updated"),
            text: t("customerReturns.validatedSuccess"),
          });
          dispatch(customerReturnActions.list({ ...filters }));
        },
      };
      dispatch(customerReturnActions.validate(payload));
    });
  };

  const handleDeleteReturn = (returnItem) => {
    Swal.fire({
      title: t("common.confirm"),
      text: t("customerReturns.confirmDelete"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("common.yes"),
      cancelButtonText: t("common.no"),
    }).then((result) => {
      if (!result.isConfirmed) return;

      const payload = {
        id: returnItem.id,
        onSuccess: () => {
          Swal.fire({
            icon: "success",
            title: t("common.deleted"),
            text: t("customerReturn.deletedSuccess"),
          });
          dispatch(customerReturnActions.list({ ...filters }));
        },
      };
      dispatch(customerReturnActions.delete(payload));
    });
  };

  // ===== KPIs =====
  const totalCount = customerReturns?.length || 0;
  const totalTTC = useMemo(() => {
    return (customerReturns || []).reduce((sum, ret) => sum + (Number(ret?.montantTTC) || 0), 0);
  }, [customerReturns]);

  const countDraft = useMemo(() => (customerReturns || []).filter((x) => x?.statut === "BROUILLON").length, [customerReturns]);
  const countValide = useMemo(() => (customerReturns || []).filter((x) => x?.statut === "VALIDE").length, [customerReturns]);
  const countAnnule = useMemo(() => (customerReturns || []).filter((x) => x?.statut === "ANNULE").length, [customerReturns]);

  const statusBadge = (statut) => {
    if (statut === "BROUILLON") return <Badge color="warning" pill className="pos-pill">Brouillon</Badge>;
    if (statut === "VALIDE") return <Badge color="success" pill className="pos-pill">Validé</Badge>;
    if (statut === "ANNULE") return <Badge color="danger" pill className="pos-pill">Annulé</Badge>;
    return <Badge color="secondary" pill className="pos-pill">{statut || "-"}</Badge>;
  };

  return (
    <Container fluid className="pos-page">
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        fullName={fullName}
        activeTab={activeTab}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(CBL_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "INVOICES") navigate(CPMT_INVOICES_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_PAGE);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
          if (tab === "TRANSFER") navigate(CPMT_TRANSFER_PAGE);
        }}
      />

      {/* ===== POS TOOLBAR ===== */}
      <Card className="pos-toolbar-card">
        <CardBody className="pos-toolbar-body">
          <div className="pos-toolbar-left">
            <div className="pos-title">{t("customerReturns.list") || "Retours clients"}</div>

          </div>

          <div className="pos-toolbar-right">
            {/* <Button
              color="secondary"
              className="pos-toolbar-btn"
              onClick={clearFilters}
              title={t("common.clearFilters")}
            >
              <FaFilter className="me-2" />
              Reset
            </Button> */}

            <Button
              color="primary"
              className="pos-toolbar-btn-primary"
              onClick={() => navigate(RETOUR_FORM_NEW)}
            >
              <FaPlus className="me-2" />
              {t("customerReturns.new") || "Nouveau retour"}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* ===== FILTER BAR ===== */}
      <Card className="pos-card mt-2">
        <CardBody className="pos-card-body">
          <Row className="g-2 align-items-end">
            <Col lg={6} md={6} sm={12}>
              <FormGroup className="mb-0">
                <Label className="pos-label">{t("reglement.search")}</Label>
                <div className="pos-input-group">
                  <Input
                    innerRef={searchInputRef}
                    value={filters.query}
                    readOnly
                    inputMode="none"
                    placeholder={t("customerReturns.searchPlaceholder")}
                    className="pos-input text-uppercase"
                    onClick={() => setShowSearchKeypad(true)}
                  />

                  <Button color="primary" type="button" className="pos-side-btn" onClick={() => setShowSearchKeypad(true)}>
                    <FaSearch />
                  </Button>

                  {filters.query ? (
                    <Button
                      color="light"
                      type="button"
                      className="pos-side-btn"
                      onClick={() => {
                        setFilters((f) => ({ ...f, query: "" }));
                        dispatch(customerReturnActions.list({ ...filters, query: "" }));
                      }}
                    >
                      <FaTimes />
                    </Button>
                  ) : null}
                </div>
              </FormGroup>
            </Col>

            <Col lg={3} md={3} sm={6} xs={6}>
              <FormGroup className="mb-0">
                <Label className="pos-label">Date début</Label>
                <Input
                  type="date"
                  className="form-control pos-input"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                />
              </FormGroup>
            </Col>

            <Col lg={3} md={3} sm={6} xs={6}>
              <FormGroup className="mb-0">
                <Label className="pos-label">Date fin</Label>
                <Input
                  type="date"
                  className="form-control pos-input"
                  value={filters.dateTo}
                  onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* ===== LIST ===== */}
      <Card className="pos-list-card mt-2 shadow-sm border-0">
        <CardBody className="pos-card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">{t("common.loading")}</div>
            </div>
          ) : !customerReturns || customerReturns.length === 0 ? (
            <div className="text-center py-5 text-muted">{t("customerReturns.noReturns")}</div>
          ) : (
            <div className="pos-list">
              {(customerReturns || []).map((returnItem) => (
                <div key={returnItem.id} className="pos-list-row">
                  <div className="left">
                    <div className="top">
                      <strong className="numero">{returnItem.numeroRetour}</strong>
                      {statusBadge(returnItem.statut)}
                    </div>

                    <div className="meta">
                      <span>
                        <FaUser className="me-1" />
                        {returnItem.clientNom}
                      </span>
                      <span>
                        <FaCalendarAlt className="me-1" />
                        {new Date(returnItem.dateOperation).toLocaleDateString("fr-FR")}
                      </span>
                      {returnItem.numBL ? (
                        <span>
                          <FaReceipt className="me-1" />
                          BL: {returnItem.numBL}
                        </span>
                      ) : null}
                      <span>{returnItem.createdBy || "N/A"}</span>
                    </div>

                    <div className="amounts">
                      <span className="ttc">{formatPrice(returnItem.montantTTC, false)} DH</span>
                    </div>
                  </div>

                  <div className="right d-flex gap-1 mt-2 flex-wrap">
                    {/* <Button
                      className="pos-icon-btn pos-pdf"
                      title={t("customerReturns.generatePdf")}
                      onClick={() => generateReturnReceipt(returnItem)}
                    >
                      <FaFilePdf />
                    </Button> */}

                    {returnItem.statut === "BROUILLON" && (
                      <Button
                        className="pos-icon-btn pos-confirm"
                        title={t("customerReturns.validate")}
                        onClick={() => handleValidateReturn(returnItem)}
                      >
                        <FaCheckCircle />
                      </Button>
                    )}

                    <Button
                      className="pos-icon-btn pos-edit"
                      title={t("customerReturns.edit")}
                      onClick={() => handleEdit(returnItem)}
                    >
                      <FaEdit />
                    </Button>

                    {returnItem.statut === "BROUILLON" && (
                      <Button
                        className="pos-icon-btn pos-delete"
                        title={t("customerReturns.delete")}
                        onClick={() => handleDeleteReturn(returnItem)}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              <div className="pos-list-footer">
                <span className="fw-bold">{t("common.totals")}</span>
                <span className="fw-bold">{formatPrice(totalTTC, false)} DH</span>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ===== MODAL FORM ===== */}
      <Modal
        isOpen={showModal}
        toggle={handleCloseModal}
        size="xl"
        className="modal-fullscreen-lg-down"
        style={{ maxWidth: "95%", width: "95%", margin: "10px auto" }}
      >
        <ModalHeader toggle={handleCloseModal}>
          {selectedReturn ? `${t("customerReturns.edit")} N° ${selectedReturn.numeroRetour}` : t("customerReturns.new")}
        </ModalHeader>
        <ModalBody>
          <CustomerReturnForm customerReturn={selectedReturn} />
        </ModalBody>
      </Modal>

      {/* ===== SEARCH KEYPAD ===== */}
      <POSKeypadModal
        isOpen={showSearchKeypad}
        toggle={() => setShowSearchKeypad(false)}
        value={filters.query}
        onChange={(val) => {
          const value = (val || "").toUpperCase();
          setFilters((prev) => ({ ...prev, query: value }));
          debouncedSearch(value);
        }}
        onSearch={() => setShowSearchKeypad(false)}
        title={t("reglement.search") || "Recherche"}
      />
    </Container>
  );
};

export default RetourClientList;
